<template>
  <span
    class="inline-flex items-center gap-1 w-fit whitespace-nowrap"
    :class="{ 'cursor-pointer': isClickable }"
  >
    <TIcon
      :icon="['fas', 'star']"
      :size="size"
      fixed-width
      :class="iconClasses"
    />
    <span :class="valueClasses">
      {{ formatWithPrecision(rating) }}
    </span>
    <span
      v-if="numberOfReviews"
      :class="reviewsClasses"
    >
      (<span :class="{ underline: isClickable }">
        {{ isCompact ? numberOfReviews : t('quantityReviews', numberOfReviews) }}
      </span>)
    </span>
  </span>
</template>

<script setup lang="ts">
import type { RatingSize, RatingVariant } from './types'

const props = withDefaults(defineProps<{
  rating?: number
  numberOfReviews?: number
  size?: RatingSize
  max?: number
  iconVariant?: RatingVariant
  isCompact?: boolean
  isClickable?: boolean
}>(), {
  rating: 0,
  size: 'md',
  max: 5,
  iconVariant: 'highlight',
})

const { t } = useI18n()

const iconClasses = computed(() => {
  return [
    {
      'text-secondary': props.iconVariant === 'highlight' && props.rating > 0,
      'text-primary-800': props.iconVariant === 'primary' && props.rating > 0,
      'text-gray-200': props.rating === 0,
    },
  ]
})

const valueClasses = computed(() => {
  return [
    'text-primary-950 font-semibold',
    {
      'text-sm': props.size === 'sm',
      'text-base': props.size === 'md',
      'text-lg': props.size === 'lg',
    },
  ]
})

const reviewsClasses = computed(() => {
  return [
    'text-gray-500',
    {
      'text-xs': props.size === 'sm',
      'text-sm': props.size === 'md',
      'text-base': props.size === 'lg',
    },
  ]
})
</script>

<i18n lang="json">
  {
    "en": {
      "quantityReviews": "0 reviews | 1 review | {n} reviews"
    },
    "fr": {
      "quantityReviews": "0 avis | 1 avis | {n} avis"
    }
  }
  </i18n>
